import { useQuery } from '@tanstack/react-query'
import { usePage, useWebsiteSettings } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { getWebsitePages } from 'src/editor/api/website-pages-api'
import { CreatePageModal } from 'src/editor/layout/sidebar/pages-management/create-page-modal'
import { PlusIcon } from 'src/editor/shared/icons/plus-icon'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { PageElement } from 'src/editor/shared/ui-lib/page-element'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'

export const PagesManagement = () => {
  const { t } = useTranslation()
  const [isCreatePageModalVisible, setCreatePageModalVisible] = useState(false)
  const websiteLocale = usePage(pageSelectors.getPageLocale) as PageLocaleEnum
  const websiteId = useWebsiteSettings(getWebsiteId)

  const { data, isFetching } = useQuery({
    queryKey: ['websiteTheme', websiteId],
    queryFn: websiteId ? () => getWebsitePages(websiteId) : undefined,
    staleTime: 1000 * 60 * 5,
    enabled: !!websiteId,
  })

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div className="inline-flex gap-3">
          <span className="text-heading-4">{t('pages_management.title')}</span>
        </div>
        <GhostIconButton
          onClick={() => setCreatePageModalVisible(true)}
          icon={<PlusIcon />}
          title={t('pages_management.create_page')}
        />
      </div>
      <div className="flex flex-col gap-1">
        {isFetching && <Skeleton height={64} count={3} />}
        {!isFetching &&
          (data?.pages?.length ? (
            data.pages.map(({ id, pageType, locales }) => {
              const localizedPage = locales.find(
                ({ locale }) => locale === websiteLocale,
              )

              return (
                <PageElement
                  key={id}
                  pageId={id}
                  name={localizedPage?.name ?? ''}
                  type={pageType}
                />
              )
            })
          ) : (
            <span className="text-M text-textTertiary">
              {t('pages_management.no_pages_in_language', {
                language: t(localeNames[websiteLocale]),
              })}
            </span>
          ))}
      </div>
      <CreatePageModal
        isVisible={isCreatePageModalVisible}
        onClose={() => setCreatePageModalVisible(false)}
        websiteId={websiteId}
        websiteLocale={websiteLocale}
      />
    </>
  )
}
